import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from "react-router-dom";
import { fetchETFs } from '../../api/tickersApi';
import { flattenEtfPositions, calculateOverlap } from "../../lib/etfTools";

const useEtfMatrix = () => {
    const [selectedTickers, setSelectedTickers] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [filters, setFilters] = useState({ search: [], sector: [] });
    const [positionCollectionMode, setPositionCollectionMode] = useState('matrix'); // ['matrix', 'consolidated']

    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const { positions, status } = useSelector(state => state.tickers.etfs);
    const etfColors = useSelector(state => state.appStates.etfColors);

    const flattenedPositions = useMemo(() => positions ? flattenEtfPositions(Object.values(positions)) : [], [positions]);
    const overlapData = useMemo(() => flattenedPositions ? calculateOverlap(flattenedPositions) : null, [flattenedPositions]);

    useEffect(() => {
        const etfsParam = searchParams.get('etfs');
        if (etfsParam) {
            const etfsSymbols = etfsParam.split(',').filter(Boolean);
            if (etfsSymbols.length > 0) {
                const etfsToFetch = etfsSymbols.map(symbol => ({ symbol }));
                handleTickersSubmit(etfsToFetch);
            }
        }
    }, [searchParams]);

    useEffect(() => {
        const tickersToFetch = selectedTickers.filter(ticker => !positions.hasOwnProperty(ticker.symbol));
        if (tickersToFetch.length > 0) {
            dispatch(fetchETFs(tickersToFetch.map(t => t.symbol)));
        }
    }, [dispatch, selectedTickers, positions]);

    const handleTickersSubmit = (newTickers) => {
        const uniqueNewTickers = newTickers.filter(newTicker => !selectedTickers.some(ticker => ticker.symbol === newTicker.symbol));
        setSelectedTickers(prevTickers => prevTickers.concat(uniqueNewTickers));
        setShowSearch(false);
    };

    const handleRemoveTicker = (ticker) => {
        setSelectedTickers(prevTickers => prevTickers.filter(t => t.symbol !== ticker.symbol));
    };

    const handleReset = () => {
        setSelectedTickers([]);
    };

    const handleAddETFsClick = () => {
        setShowSearch(true);
    };

    const handleSelectGroup = (etfs) => {
        const etfObjects = etfs.map(etf => ({ symbol: etf.toLowerCase() }));
        handleTickersSubmit(etfObjects);
    };

    const addTickerToSearch = (symbol) => {
        const searchFilters = [...filters.search, symbol];
        setFilters(prevFilters => ({
            ...prevFilters,
            search: searchFilters
        }));
    };

    const removeTickerFromSearch = (symbol) => {
        const searchFilters = filters.search.filter(s => s !== symbol);
        setFilters(prevFilters => ({
            ...prevFilters,
            search: searchFilters
        }));
    };

    const clearTickersFromSearch = () => {
        setFilters(prevFilters => ({
            ...prevFilters,
            search: []
        }));
    };

    const filteredPositions = Object.fromEntries(
        Object.entries(positions).filter(([symbol]) =>
            selectedTickers.some(ticker => ticker.symbol === symbol)
        )
    );

    return {
        selectedTickers,
        showSearch,
        filters,
        positionCollectionMode,
        positions,
        etfColors,
        overlapData,
        flattenedPositions,
        filteredPositions,
        handleTickersSubmit,
        handleRemoveTicker,
        handleReset,
        handleAddETFsClick,
        handleSelectGroup,
        addTickerToSearch,
        removeTickerFromSearch,
        clearTickersFromSearch,
        setShowSearch,
    };
};

export default useEtfMatrix;
